import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Chip, ClickAwayListener } from "@mui/material";
import UBInput from "../UBInput";
import {
  CloseIcon,
  InputDropdownIcon,
  PlusBlueIcon,
} from "../../assets/icons/icon";
import styles from "./styles.module.css";
import ParagraphText from "../ParagraphText";
import { LanguageContext } from "../../context/LanguageContext";
import UBButton from "../UBButton";
import SearchSkeleton from "../../components/common/SearchSkeleton";

const TabAutoComplete = ({
  label,
  tabList = [],
  list = {},
  handleSelect,
  value,
  handleCreate,
  placeholder,
  disabled = false,
  asCreate = "",
  required,
  handleStartLogEvent = () => {},
  id = "",
  manuallyFocusInput = false,
  handleManuallyInput = () => {},
  defaultOpen = false,
  hideCloseIcon = false,
  hideSuffixIcon = false,
  prefixIcon = null,
  className,
  onFocus,
  onBlur,
  onOpenChange,
  requireSearchToCreate = false,
  error = "",
  onInputChange,
  loading = false,
  hideEmptySuggestions = false,
  maxLength,
  onKeyDown,
  listHeader = "",
  richValue = null,
  manualTrigger = false,
  suffixLabel = null,
  numericWhenClosed = false,
  // With manualTrigger, typing normally never opens the panel (only the
  // suffix/forceShowList does, e.g. "Contacts"). Opt into this to also open
  // it once the user has actually typed something (e.g. a live phone-number
  // search), without changing the forceShowList-only behavior for existing
  // manualTrigger callers.
  openOnType = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [isSearching, setIsSearching] = useState(false);
  const [forceShowList, setForceShowList] = useState(false);
  const [search, setSearch] = useState("");
  const isListOpen = manualTrigger
    ? forceShowList || (openOnType && open && !!search)
    : open;

  useEffect(() => {
    // Second arg is forceShowList specifically, so a manualTrigger caller that
    // also sets openOnType can still tell "browsing via the manual trigger"
    // apart from "panel open because the user is typing".
    onOpenChange?.(isListOpen, forceShowList);
  }, [isListOpen, forceShowList]);
  const [I18n] = useContext(LanguageContext);
  const [selectedTab, setSelectedTab] = useState(
    tabList.length > 0 ? tabList[0].value : "",
  ); // Default tab is 'Company'
  const [highlightedIndex, setHighlightedIndex] = useState(null); // For keyboard navigation
  const inputRef = useRef();
  const suggestionsRef = useRef(); // Reference to the suggestion container
  const tabRef = useRef(false);
  useEffect(() => {
    if (!open) {
      setSearch("");
      setIsSearching(false);
      inputRef.current?.blur();
    }
  }, [open]);

  useEffect(() => {
    if (manuallyFocusInput && inputRef?.current) inputRef.current.focus();
    else {
      inputRef.current.blur();
      handleManuallyInput();
    }
  }, [manuallyFocusInput]);

  const handleTabChange = (newValue) => {
    tabRef.current = true;
    setSelectedTab(newValue);
    if (inputRef?.current) inputRef.current.focus();
  };

  const handleClickAway = (event) => {
    if (inputRef.current && !inputRef.current.contains(event.target)) {
      setOpen(false);
      setForceShowList(false);
      handleManuallyInput();
      setSearch("");
    }
  };

  const filterList =
    (list[selectedTab] || list || []).length > 0
      ? (list[selectedTab] || list || []).filter((item) =>
          item.value.toLowerCase().includes(search.toLowerCase()),
        )
      : [];

  const isInputFocused = () => {
    return inputRef.current === document.activeElement;
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      tabRef.current = false;
      if (!isListOpen) return;
      if (event.key === "Tab" && search && !isInputFocused()) {
        event.preventDefault();
        setOpen(false);
        handleManuallyInput();
      }

      if (event.key === "ArrowDown") {
        // Move to the next option
        setHighlightedIndex((prev) =>
          prev === null || prev === filterList.length - 1 ? 0 : prev + 1,
        );
      } else if (event.key === "ArrowUp") {
        // Move to the previous option
        setHighlightedIndex((prev) =>
          prev === null || prev === 0 ? filterList.length - 1 : prev - 1,
        );
      } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        setHighlightedIndex(null);
        // Switch tabs with left or right arrow
        const currentTabIndex = tabList.findIndex(
          (tab) => tab.value === selectedTab,
        );
        let nextTabIndex = currentTabIndex;

        if (event.key === "ArrowLeft") {
          setHighlightedIndex(null);
          // Move to the previous tab
          nextTabIndex =
            currentTabIndex === 0 ? tabList.length - 1 : currentTabIndex - 1;
        } else if (event.key === "ArrowRight") {
          setHighlightedIndex(null);
          // Move to the next tab
          nextTabIndex =
            currentTabIndex === tabList.length - 1 ? 0 : currentTabIndex + 1;
        }

        if (tabList[nextTabIndex]) {
          handleTabChange(tabList[nextTabIndex].value);
        }
      } else if (event.key === "Enter") {
        // Select the highlighted item
        if (highlightedIndex !== null) {
          event.preventDefault();
          handleSelect(filterList[highlightedIndex], selectedTab);
          setOpen(false);
          setForceShowList(false);
          handleManuallyInput();
          setSearch("");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    inputRef,
    search,
    isInputFocused,
    tabList,
    selectedTab,
    filterList,
    highlightedIndex,
    tabRef,
    isListOpen,
  ]);

  const handleSearchChange = (_, rawValue) => {
    // While the list isn't open, restrict free typing to digits (phone number entry);
    // once opened (e.g. via a manualTrigger suffix like "Contacts"), allow searching by name.
    // With openOnType the panel opens *because* the user is typing a phone number, so the
    // digits-only restriction must keep applying for the whole session, not just pre-open.
    const nextValue =
      numericWhenClosed && (!isListOpen || openOnType)
        ? rawValue.replace(/\D/g, "")
        : rawValue;
    setIsSearching(true);
    setSearch(nextValue);
    setHighlightedIndex(null); // Reset highlighted index when search changes
    // Only force-open while typing for free-text search flows (e.g. bill consumer),
    // or for a manualTrigger field that explicitly opted into it via openOnType.
    if (onInputChange && (!manualTrigger || openOnType)) setOpen(true);
    onInputChange?.(nextValue);
  };

  const showSuggestionPanel =
    isListOpen &&
    (!hideEmptySuggestions || loading || filterList.length > 0 || !!handleCreate);

  // Scroll the selected item into view
  const scrollToHighlighted = () => {
    if (suggestionsRef.current && highlightedIndex !== null) {
      const highlightedElement =
        suggestionsRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          behavior: "auto",
          block: "nearest",
        });
      }
    }
  };

  // Scroll when highlightedIndex changes
  React.useEffect(() => {
    scrollToHighlighted();
  }, [highlightedIndex]);

  return (
    <Box className={`${styles.wrapper} ${suffixLabel ? styles.wrapperWithSuffixLabel : ""} ${className}`}>
      {label ? (
        <ParagraphText
          className={"fontSize14 newFontColor InputLabelStyle mb-2"}
        >
          <span>
            {label}
            {required && (
              <span className="foundationErrorRed marginLeft2">*</span>
            )}
          </span>
        </ParagraphText>
      ) : null}
      <Box
        className={`searchInputBox ${styles.searchInputBox} ${
          showSuggestionPanel ? styles.suggestionOpen : ""
        }`}
      >
        <UBInput
          inputId={`${id}_inputId`}
          disabled={disabled}
          value={open ? (isSearching ? search : value) : (value ?? "")}
          inputClassname={richValue && !open ? styles.hiddenInputText : ""}
          inputRef={inputRef}
          prefix={prefixIcon}
          maxLength={maxLength}
          onKeyDown={onKeyDown}
          suffix={
            suffixLabel ? (
              !(value || search) && (
                <ParagraphText
                  className={`fontSize14 fontWeight500 barclayBlueColor textDecorationUnerline cursorPointer ${styles.suffixLabelText}`}
                >
                  {suffixLabel}
                </ParagraphText>
              )
            ) : (
              !hideSuffixIcon &&
              !(value || search) && (
                <InputDropdownIcon
                  className={`cursorPointer ${open ? styles.inputDropdownIconOpen : ""}`}
                  height={20}
                  width={20}
                />
              )
            )
          }
          handleSuffix={
            suffixLabel
              ? () => {
                  handleStartLogEvent();
                  setForceShowList((prev) => {
                    const next = !prev;
                    if (next) {
                      setSearch("");
                      setIsSearching(false);
                    }
                    return next;
                  });
                }
              : () => {}
          }
          placeholder={placeholder}
          onChange={handleSearchChange}
          onInputContainerClick={() => {
            if (disabled || manualTrigger) return;
            if (open) {
              setSearch("");
              setOpen(false);
              handleManuallyInput();
            } else {
              setTimeout(() => {
                handleStartLogEvent();
                setOpen(true);
              }, 100);
            }
          }}
          onFocus={() => {
            if (onFocus) {
              onFocus();
            }
            setTimeout(() => {
              setOpen(true);

              handleStartLogEvent();
            }, 300);
          }}
          onBlur={() => {
            if (onBlur) {
              onBlur();
            }
            if (!(search && filterList.length === 0))
              setTimeout(() => {
                if (!tabRef.current) {
                  setOpen(false);
                  handleManuallyInput();
                }
              }, 500);
          }}
          error={{ text: error, show: !!error }}
        />
        {richValue && !open ? (
          <Box className={styles.richValueOverlay}>{richValue}</Box>
        ) : null}
        {value || search ? (
          <Box
            position={"absolute"}
            right={"5px"}
            top={"4px"}
            className="cursorPointer"
          >
            {!(disabled || hideCloseIcon) && (
              <CloseIcon
                id={`${id}_close_icon`}
                className="closeIcon"
                onClick={() => {
                  if (!open) {
                    handleSelect(null);
                    return;
                  }
                  setSearch("");
                  onInputChange?.("");
                }}
              />
            )}
          </Box>
        ) : (
          ""
        )}
      </Box>
      {showSuggestionPanel && (
        <ClickAwayListener
          onClickAway={handleClickAway}
          id={`${id}_click_away`}
        >
          <Box className={`suggestionContainer ${styles.suggestionContainer}`}>
            {/* Tabs */}
            {tabList.length > 0 ? (
              <Box className={styles.chipWrapper}>
                {tabList.map((item, index) => (
                  <Chip
                    tabIndex={-1}
                    key={item.value || index}
                    className={`${styles.chip} ${
                      item.value === selectedTab ? styles.selected : ""
                    }`}
                    label={item.label}
                    variant="outlined"
                    onClick={() => handleTabChange(item.value)}
                    id={`${id}_${item.value}`}
                  />
                ))}
              </Box>
            ) : null}

            {/* Suggestions */}
            <Box
              className={`${styles.itemSuggestInner} itemSuggestInner generic-scrollbar`}
              ref={suggestionsRef} // Attach the ref here
            >
              {loading ? (
                <SearchSkeleton />
              ) : filterList.length === 0 ? (
                <>
                  {/* No result text */}
                  <Box
                    className={styles.noResultFoundBox}
                    id={`${id}_no_found`}
                  >
                    <ParagraphText className="fontSize14 foundationInk300">
                      {list.length > 0
                        ? I18n.no_result_found
                        : I18n.start_typing_to_create_one}
                    </ParagraphText>
                  </Box>

                  {/* Create new option */}
                  {handleCreate && (!requireSearchToCreate || search) ? (
                    <UBButton
                      tabIndex={0}
                      className={styles.addNewItemWrapper}
                      id={`${id}_create_as_new`}
                      onClick={() => {
                        handleCreate(search, selectedTab);
                        setOpen(false);
                        setForceShowList(false);
                        handleManuallyInput();
                        setSearch("");
                      }}
                      title={
                        <ParagraphText className="fontSize12 foundationBlueNormal pointer">
                          <span className={styles.plusIcon}>
                            <PlusBlueIcon width={12} height={12} />
                          </span>{" "}
                          {search ? (
                            <>
                              <span>{I18n.create}</span>
                              <span
                                className={`marginLeft5 marginRight5 ${styles.createSearchText}`}
                                title={search}
                              >{`"${search}"`}</span>
                              <span>{`as ${tabList.length > 0 ? selectedTab : asCreate}`}</span>
                            </>
                          ) : (
                            <span>{`${I18n.create} as ${tabList.length > 0 ? selectedTab : asCreate}`}</span>
                          )}
                        </ParagraphText>
                      }
                    />
                  ) : null}
                </>
              ) : (
                <>
                  {listHeader ? (
                    <ParagraphText className={`${styles.listHeader} listHeader`}>
                      {listHeader}
                    </ParagraphText>
                  ) : null}
                  {filterList.map((item, index) => (
                  <Box
                    id={`${id}_select${index}`}
                    key={index}
                    onClick={() => {
                      handleSelect(item, selectedTab);
                      setOpen(false);
                      setForceShowList(false);
                      handleManuallyInput();
                      setSearch("");
                    }}
                    className={`${styles.singleItemSuggest} singleItemSuggest ${
                      item?.subLabel ? `${styles.singleItemSuggestWithSub} singleItemSuggestWithSub` : ""
                    } hover_color_foundation_Blue transition_background_color pointer ${
                      highlightedIndex === index ? `${styles.highlighted} highlighted` : ""
                    }`} // Apply highlighted class
                  >
                    {item.img ? (
                      <Box className={`${styles.iconBox} iconBox`}>
                        <span className="imageDiv">{item.img}</span>
                      </Box>
                    ) : null}
                    {item?.subLabel ? (
                      <Box className={`${styles.itemTextRow} itemTextRow`}>
                        <ParagraphText className="fontSize14 newFontColor flex1 truncateOneLine dropdownLabel">
                          {item?.label ?? ""}
                        </ParagraphText>
                        <ParagraphText className={`fontSize13 newLightColor ${styles.itemSubLabel} itemSubLabel`}>
                          {item.subLabel}
                        </ParagraphText>
                      </Box>
                    ) : (
                      <ParagraphText className="fontSize14 newFontColor flex1 truncateOneLine dropdownLabel">
                        {item?.label ?? ""}
                      </ParagraphText>
                    )}
                  </Box>
                  ))}
                </>
              )}
            </Box>
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default TabAutoComplete;
